import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { EditionArtwork, artworkImageUris } from './generated/EditionArtwork';
import { editionCaptureSize } from './generated/captureSize';
import { archiveScenes } from './generated/model';
import { ImageContext, safeArtworkUrl } from './webSvg';
import regularFont from '../../app-ui/fonts/Pretendard-Regular.otf';
import semiboldFont from '../../app-ui/fonts/Pretendard-SemiBold.otf';

const LIMIT = 10 * 1024 * 1024;
const abort = (signal) => {
  if (signal.aborted) throw signal.reason ?? new Error('Export cancelled');
};
function dataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Image read failed'));
    reader.readAsDataURL(blob);
  });
}
export async function fetchExportAsset(url, { signal, image = false, fetcher = fetch } = {}) {
  if (image && !safeArtworkUrl(url)) throw new Error('Unsupported image URL');
  // Bundlers may inline fonts/images. Decode locally, without a data: fetch
  // (which strict preview connect-src correctly disallows).
  if (url.startsWith('data:')) {
    const match = /^data:[a-z0-9.+/-]+;base64,([a-z0-9+/=]+)$/i.exec(url);
    if (!match || match[1].length > LIMIT * 1.34) throw new Error('Invalid inline asset');
    const size = atob(match[1]).length;
    if (!size || size > LIMIT) throw new Error('Invalid inline asset size');
    if (signal?.aborted) throw signal.reason ?? new Error('Export cancelled');
    return { uri: url, size };
  }
  const response = await fetcher(url, {
    signal,
    credentials: 'omit',
    mode: 'cors',
    referrerPolicy: 'no-referrer',
  });
  if (!response.ok) throw new Error(`Asset unavailable (${response.status})`);
  const type = response.headers.get('content-type')?.split(';')[0].trim().toLowerCase();
  // Do not put arbitrary SVG/HTML or active content inside an exported document.
  if (image && !['image/png', 'image/jpeg', 'image/webp'].includes(type))
    throw new Error('Unsupported image type');
  if (Number(response.headers.get('content-length')) > LIMIT) throw new Error('Asset too large');
  let blob;
  if (response.body?.getReader) {
    const reader = response.body.getReader(),
      chunks = [];
    let total = 0;
    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        total += value.byteLength;
        if (total > LIMIT) throw new Error('Asset too large');
        chunks.push(value);
      }
    } catch (error) {
      await reader.cancel().catch(() => {});
      throw error;
    } finally {
      reader.releaseLock();
    }
    blob = new Blob(chunks, { type: type || 'application/octet-stream' });
  } else {
    blob = await response.blob();
    if (blob.size > LIMIT) throw new Error('Asset too large');
  }
  if (!blob.size) throw new Error('Empty asset');
  return { uri: await dataUrl(blob), size: blob.size };
}
async function decodeImage(src, signal) {
  abort(signal);
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const clean = () => {
      signal.removeEventListener('abort', cancel);
      img.onload = null;
      img.onerror = null;
    };
    const cancel = () => {
      clean();
      img.src = '';
      reject(signal.reason ?? new Error('Export cancelled'));
    };
    img.onload = () => {
      clean();
      if (
        !img.naturalWidth ||
        !img.naturalHeight ||
        img.naturalWidth * img.naturalHeight > 40000000
      )
        reject(new Error('Invalid image dimensions'));
      else resolve(img);
    };
    img.onerror = () => {
      clean();
      reject(new Error('Image could not be decoded'));
    };
    signal.addEventListener('abort', cancel, { once: true });
    img.src = src;
  });
}
export async function exportArtworkPng(props, { signal, timeout = 15000, fetcher = fetch } = {}) {
  if (!archiveScenes.includes(props.scene) || !['story', 'card'].includes(props.format))
    throw new Error('Invalid export scene or format');
  const controller = new AbortController();
  const cancel = () => controller.abort(signal?.reason ?? new Error('Export cancelled'));
  const timer = setTimeout(() => controller.abort(new Error('Export timed out')), timeout);
  if (signal?.aborted) cancel();
  else signal?.addEventListener('abort', cancel, { once: true });
  let objectUrl;
  try {
    abort(controller.signal);
    const urls = artworkImageUris(props);
    if (urls.length > 40) throw new Error('Too many image assets');
    const images = {};
    let total = 0;
    // Bound concurrency and memory; no cached member images survive this export.
    for (let start = 0; start < urls.length; start += 4) {
      const sizes = await Promise.all(
        urls.slice(start, start + 4).map(async (url) => {
          const asset = await fetchExportAsset(url, {
            signal: controller.signal,
            image: true,
            fetcher,
          });
          await decodeImage(asset.uri, controller.signal);
          images[url] = asset.uri;
          return asset.size;
        }),
      );
      total += sizes.reduce((sum, size) => sum + size, 0);
      if (total > 40 * 1024 * 1024) throw new Error('Archive images too large');
    }
    const fonts = await Promise.all(
      [regularFont, semiboldFont].map((url) =>
        fetchExportAsset(url, { signal: controller.signal, fetcher }),
      ),
    );
    // The same bundled font is embedded for preview-independent PNG rendering.
    const fontCss = fonts
      .map(
        (font, i) =>
          `@font-face{font-family:${i ? 'EditionSemiBold' : 'EditionRegular'};font-style:normal;font-weight:400;src:url(${font.uri}) format('opentype');}`,
      )
      .join('');
    const size = editionCaptureSize(props.format, 'web', 1);
    let svg = renderToStaticMarkup(
      <ImageContext.Provider value={images}>
        <EditionArtwork
          {...props}
          width={size.width}
          motionProgress={1}
          animateCharts={false}
          onImageLoad={undefined}
        />
      </ImageContext.Provider>,
    );
    svg = svg
      .replace(/height="[^"]*"/, `height="${size.height}"`)
      .replace(/(<svg\b[^>]*>)/, `$1<defs><style>${fontCss}</style></defs>`);
    abort(controller.signal);
    objectUrl = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }));
    const rendered = await decodeImage(objectUrl, controller.signal);
    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas unavailable');
    context.drawImage(rendered, 0, 0, size.width, size.height);
    const blob = await new Promise((resolve, reject) =>
      canvas.toBlob(
        (value) => (value ? resolve(value) : reject(new Error('PNG encoding failed'))),
        'image/png',
      ),
    );
    canvas.width = 0;
    canvas.height = 0;
    abort(controller.signal);
    return { blob, ...size };
  } catch (error) {
    // A fetch/decode may surface its own AbortError; retain the caller's
    // cancellation or timeout reason instead of misreporting a network fault.
    if (controller.signal.aborted) throw controller.signal.reason ?? error;
    throw error;
  } finally {
    clearTimeout(timer);
    controller.abort();
    signal?.removeEventListener('abort', cancel);
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
}
export function downloadArtwork(blob, name) {
  const url = URL.createObjectURL(blob),
    link = document.createElement('a');
  link.href = url;
  link.download = name.replace(/[^\p{L}\p{N}._-]/gu, '_');
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Keep the URL alive until browsers have consumed the download task.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
